import classNames from 'classnames';
import { IconWithText } from 'common/icon/icon-with-text.js';
import { IconNameV1 } from 'common/icon/iconV1.js';
import { PanelBodyMarineListSubType } from 'components/weather-warning/panel-body-marine-list-subtype.js';
import { ManageUniqueWarningSlug } from 'components/weather-warning/utils/manageSlug.js';
import { IWarning } from 'redux-state/weather-warnings/types.js';
import { formatJSONTime } from 'utilities/date.js';
import { getFirstOfArr } from 'utilities/isType.js';
import { getSeverityInfo } from 'utilities/weather-warnings/warning-severity.js';
import { buildDataSlug } from 'utilities/weather-warnings/warnings.js';

interface IProps {
  warnings: IWarning[];
}

const MAX_TIMESTAMP = 8640000000000000;

type SplitMarineWarnValues = { warnings: IWarning[]; issuedAt: string; nextIssuedAt: string };
type SplitMarineWarnings = { recreational: SplitMarineWarnValues; coastal: SplitMarineWarnValues };
const splitMarineWarningsBySubType = (warnings: IWarning[]) => {
  return warnings.reduce<SplitMarineWarnings>(
    (acc, warning) => {
      const warningIssuedAtTime = warning.issuedAt ? formatJSONTime(warning.issuedAt) : new Date(MAX_TIMESTAMP);
      const warningNextIssuedAtTime = warning.nextIssueAt
        ? formatJSONTime(warning.nextIssueAt)
        : new Date(MAX_TIMESTAMP);

      if (warning.subType === 'recreational') {
        acc.recreational.warnings = acc.recreational.warnings.concat(warning);
        const thisIssuedAt = acc.recreational.issuedAt
          ? formatJSONTime(acc.recreational.issuedAt)
          : new Date(MAX_TIMESTAMP);
        const thisNextIssueAt = acc.recreational.nextIssuedAt
          ? formatJSONTime(acc.recreational.nextIssuedAt)
          : new Date(MAX_TIMESTAMP);
        if (warningIssuedAtTime.getTime() < thisIssuedAt.getTime()) {
          acc.recreational.issuedAt = warning.issuedAt;
        }
        if (warningNextIssuedAtTime.getTime() < thisNextIssueAt.getTime()) {
          acc.recreational.nextIssuedAt = warning.nextIssueAt;
        }
        return acc;
      }
      if (warning.subType === 'coastal') {
        acc.coastal.warnings = acc.coastal.warnings.concat(warning);
        const thisIssuedAt = acc.coastal.issuedAt ? formatJSONTime(acc.coastal.issuedAt) : new Date(MAX_TIMESTAMP);
        const thisNextIssueAt = acc.coastal.nextIssuedAt
          ? formatJSONTime(acc.coastal.nextIssuedAt)
          : new Date(MAX_TIMESTAMP);
        if (warningIssuedAtTime.getTime() < thisIssuedAt.getTime()) {
          acc.coastal.issuedAt = warning.issuedAt;
        }
        if (warningNextIssuedAtTime.getTime() < thisNextIssueAt.getTime()) {
          acc.coastal.nextIssuedAt = warning.nextIssueAt;
        }
        return acc;
      }
      return acc;
    },
    {
      recreational: { warnings: [], issuedAt: '', nextIssuedAt: '' },
      coastal: { warnings: [], issuedAt: '', nextIssuedAt: '' },
    },
  );
};

export const PanelBodyList = ({ warnings }: IProps) => {
  const firstWarning = getFirstOfArr(warnings);
  if (firstWarning) {
    const dataSlug = buildDataSlug(firstWarning);
    const isHighlighted = ManageUniqueWarningSlug(dataSlug);
    const { name, icon, warnLevel } = firstWarning;
    const { iconColor } = getSeverityInfo(warnLevel);
    const splitMarineWarnings = splitMarineWarningsBySubType(warnings);
    return (
      <div data-module="marine-list" className={classNames('warning-body-item', { active: isHighlighted })}>
        <IconWithText
          iconClass="negative-md-icon-inline"
          classes={'IconWithText--responsive-sm-md'}
          icon={`${icon}-${iconColor}` as IconNameV1}
        >
          <h2 className="u-mT-0 u-mB-xs">{name}</h2>
        </IconWithText>
        <PanelBodyMarineListSubType {...splitMarineWarnings.coastal} />
        <PanelBodyMarineListSubType {...splitMarineWarnings.recreational} />
      </div>
    );
  }
  return null;
};
